/**
 * The legacy crow-tour chase camera (`createScene.ts` `advanceCrowTour`) baked
 * into native SDK routes: the chase itself, and the hand-back that returns the
 * camera to where the chase took it from.
 */
import type { CameraRoute, RouteWaypoint, ScenePoint } from '@datum-sdk/scene-format';
import type { CrowTourConfig } from '../../sceneConfig';
import { smoothstep, type Vector3Like } from '../../props/crowFlight';
import {
  DEFAULT_TOUR,
  bobOffsetAt,
  sampleCrowTour,
  type CrowTourParams,
} from '../../props/crowTour';
import { EASE_IN_OUT_CUBIC } from '../nativeRouteCinematic';
import { repaceSmoothRuns } from '../routes/repaceSmoothRuns';

export const CHASE_ROUTE_ID = 'crow-chase';
export const HANDBACK_ROUTE_ID = 'crow-chase-handback';
/** One view per this much tour time: dense enough for the archway, sparse enough for C14. */
const CHASE_KNOT_SECONDS = 0.3;

export interface ChasePose {
  position: ScenePoint;
  lookAt: ScenePoint;
  fov: number;
}

const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

/**
 * The tour the bird flies, both ends pinned to the perch — nothing else holds
 * the landing to it. Null for a route too short to fly: the spline mirrors its
 * phantom points off the first and last legs.
 */
export const tourFromPerch = (
  settings: CrowTourConfig,
  perch: Vector3Like,
): CrowTourParams | null => {
  if (settings.waypoints.length < 2) return null;
  const last = settings.waypoints.length - 1;
  return {
    ...DEFAULT_TOUR,
    ...settings,
    waypoints: settings.waypoints.map((point, index) =>
      index === 0 || index === last ? { x: perch.x, y: perch.y, z: perch.z } : point,
    ),
  };
};

/** Legacy `advanceCrowTour` at tour progress `u`: where the camera sits, what it aims at. */
export const chasePoseAt = (
  params: CrowTourParams,
  settings: CrowTourConfig,
  u: number,
  entry: ChasePose,
): ChasePose => {
  const bird = sampleCrowTour(params, u);
  const lookY = bird.y - bobOffsetAt(params, u);
  const lookHeading = bird.heading - params.yawOffset;
  const { closeUp } = settings;
  const weight =
    closeUp && closeUp.span > 0
      ? smoothstep(Math.max(0, 1 - Math.abs(u - closeUp.at) / (closeUp.span / 2)))
      : 0;
  const distance = lerp(settings.cameraDistance, closeUp?.distance ?? 0, weight);
  const height = lerp(settings.cameraHeight, closeUp?.height ?? 0, weight);
  const blend =
    settings.entrySeconds > 0
      ? smoothstep(Math.min(1, (u * params.duration) / settings.entrySeconds))
      : 1;
  const [ex, ey, ez] = entry.position;
  const [tx, ty, tz] = entry.lookAt;
  return {
    position: [
      lerp(ex, bird.x - Math.sin(lookHeading) * distance, blend),
      lerp(ey, lookY + height, blend),
      lerp(ez, bird.z - Math.cos(lookHeading) * distance, blend),
    ],
    lookAt: [lerp(tx, bird.x, blend), lerp(ty, lookY, blend), lerp(tz, bird.z, blend)],
    fov: closeUp ? lerp(entry.fov, lerp(entry.fov, closeUp.fov, weight), blend) : entry.fov,
  };
};

/**
 * One smooth run of views at equal steps of `u`, so route progress is tour
 * progress at every view. The first view is the entry pose itself, so the
 * engine's fly-in onto it is instant; speeds are made continuous across the
 * views the way the flyover's are.
 */
export const buildChaseRoute = (
  params: CrowTourParams,
  settings: CrowTourConfig,
  entry: ChasePose,
): CameraRoute => {
  const count = Math.max(2, Math.round(params.duration / CHASE_KNOT_SECONDS));
  const stepMs = (params.duration / count) * 1000;
  const waypoints: RouteWaypoint[] = [];
  for (let knot = 0; knot <= count; knot++) {
    const pose = knot === 0 ? entry : chasePoseAt(params, settings, knot / count, entry);
    waypoints.push({
      kind: 'view',
      position: pose.position,
      lookAt: pose.lookAt,
      fov: pose.fov,
      duration: knot === 0 ? 1 : stepMs,
    });
  }
  const route: CameraRoute = { id: CHASE_ROUTE_ID, waypoints };
  return { ...route, waypoints: repaceSmoothRuns(route, entry.position) };
};

/**
 * Back to the entry pose with its fov — legacy passes the fov explicitly too,
 * or a tour cut short mid-close-up would leave the scene zoomed in.
 */
export const buildHandBackRoute = (entry: ChasePose, durationMs: number): CameraRoute => ({
  id: HANDBACK_ROUTE_ID,
  waypoints: [
    {
      target: entry.position,
      lookAt: entry.lookAt,
      fov: entry.fov,
      duration: durationMs,
      easing: EASE_IN_OUT_CUBIC,
    },
  ],
});
